import type { TravelLog as TravelLogData } from '@/api/client/models/TravelLog'
import type { TravelLogResponse } from '@/api/client'
import { TravelLogService } from '@/api/client'
import FileManager from '@/classes/manager/FileManager'
import { type Sendable } from '@/interfaces/Sendable'
import { type Error } from '@/types/error'
import { useEventStore } from '@/stores/EventStore'
import { report } from '@/classes/manager/ExceptionReportingManager'

type TDehydratedTravelLog = {
  travel_log_data: TravelLogData
  files: TTravelLogFile[]
  type: 'travel_log'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
}

type TTravelLogFile = {
  id: number | null
  unassigned_uploaded_file_id: number | null
  uri: string | null
  webPath: string
  mimeType: string
  name: string
}

class TravelLog implements Sendable {
  readonly type = 'travel_log'
  meta: {
    transmitted: boolean
    transmitted_at: string
    error_log: Error[]
  }
  travel_log_data: TravelLogData
  files: TTravelLogFile[]
  constructor(
    travelLogData: TravelLogData,
    files: TTravelLogFile[] = [],
    meta = {
      transmitted: false,
      transmitted_at: '',
      error_log: [] as Error[],
    },
  ) {
    this.travel_log_data = travelLogData
    this.files = files
    this.meta = meta
  }
  async send(
    handleProgress?: (completed: number, total: number) => void,
  ): Promise<{ error: false | Error }> {
    return new Promise(async (resolve, reject) => {
      const numberOfItemsToSend = this.files.length + 1
      for await (const [i, file] of this.files.entries()) {
        if (!file.unassigned_uploaded_file_id) {
          if (!file.webPath) throw new Error('TravelLog: File webPath is null')
          if (!this.travel_log_data.employee_id)
            throw new Error('TravelLog: employee_id is null')
          const fileUploadResponse = await FileManager.upload(
            file.webPath,
            this.travel_log_data.employee_id,
          )
          console.log('fileUploadResponse', fileUploadResponse)
          if (!fileUploadResponse?.data.unassigned_uploaded_file_id) {
            report(
              new Error(
                'TravelLog: file upload failed with the following response:' +
                  JSON.stringify(fileUploadResponse),
              ),
            )
            continue
          }
          file.unassigned_uploaded_file_id =
            fileUploadResponse.data.unassigned_uploaded_file_id
          handleProgress?.(i + 1, numberOfItemsToSend)
        }
      }

      const allFilesAreUploaded = this.files.reduce(
        (accumulator, currentValue) =>
          currentValue.unassigned_uploaded_file_id !== null && accumulator,
        true,
      )
      if (allFilesAreUploaded) {
        this.travel_log_data.unassigned_uploaded_file_ids = this.files.map(
          (file) => file.unassigned_uploaded_file_id || -1, // fallback to -1 should never happen but typescript does not understand the reduce above
        )

        console.log('TravelLog: sending travelLog', this.travel_log_data)
        TravelLogService.storeTravelLog(this.travel_log_data)
          .then((response) => {
            console.log('TravelLog: response', response)
            if (response.data?.id) {
              console.log(
                'TravelLog: stored travelLog with id',
                response.data.id,
              )
              this.travel_log_data.id = response.data.id
            }
            resolve({ error: false })
          })
          .catch((error) => {
            reject({
              error_message: JSON.stringify(error),
              error_code: 'TravelLog: storeTravelLog failed',
            })
          })
        handleProgress?.(numberOfItemsToSend, numberOfItemsToSend)
      }
    })
  }

  async sendUpdate(
    handleProgress?: (completed: number, total: number) => void,
  ): Promise<{ error: false | Error }> {
    return new Promise(async (resolve, reject) => {
      if (!this.travel_log_data.id)
        throw new Error('Cannot update TravelLog that has not been created.')
      const filesToSend = this.files.filter(
        (f) => !f.unassigned_uploaded_file_id,
      )

      const numberOfItemsToSend = filesToSend.length + 1

      for (const [i, file] of filesToSend.entries()) {
        if (!file.unassigned_uploaded_file_id) {
          if (!file.webPath) throw new Error('TravelLog: File webPath is null')
          if (!this.travel_log_data.employee_id)
            throw new Error('TravelLog: employee_id is null')
          const fileUploadResponse = await FileManager.upload(
            file.webPath,
            this.travel_log_data.employee_id,
          )
          console.log('fileUploadResponse', fileUploadResponse)
          if (!fileUploadResponse?.data.unassigned_uploaded_file_id) {
            report(
              new Error(
                'TravelLog: file upload failed with the following response:' +
                  JSON.stringify(fileUploadResponse),
              ),
            )
            continue
          }
          file.unassigned_uploaded_file_id =
            fileUploadResponse.data.unassigned_uploaded_file_id
          handleProgress?.(i + 1, filesToSend.length)
        }
      }

      const allFilesAreUploaded = this.files.reduce(
        (a, file) => (!!file.unassigned_uploaded_file_id || !!file.id) && a,
        true,
      )
      if (allFilesAreUploaded) {
        this.travel_log_data.unassigned_uploaded_file_ids = this.files
          .filter((f) => !f.id)
          .map(
            (file) => file.unassigned_uploaded_file_id || -1, // fallback to -1 should never happen but typescript does not understand the reduce above
          )
        this.travel_log_data.assigned_file_ids = this.files
          .filter((f) => !!f.id)
          .map((f) => f.id || -1)

        console.log('TravelLog: sending travelLog', this.travel_log_data)
        TravelLogService.updateTravelLog(this.travel_log_data)
          .then(() => {
            resolve({ error: false })
          })
          .catch((error) => {
            reject({
              error_message: JSON.stringify(error),
              error_code: 'TravelLog: storeTravelLog failed',
            })
          })
        handleProgress?.(numberOfItemsToSend, numberOfItemsToSend)
      }
    })
  }
  isReady() {
    // only send after the task was finished and the corresponding event was synced
    // in order to not have to keep server state in sync while travelLog is editable
    // and to be able to attach to event on backend side
    if (!this.travel_log_data.event_uuid) {
      report(new Error('TravelLog: event_uuid is null'))
      return false
    }
    return (
      (useEventStore().eventWasSent(this.travel_log_data.event_uuid) &&
        useEventStore().lastEvent?.uuid !== this.travel_log_data.event_uuid) ||
      !useEventStore().getEvent(this.travel_log_data.event_uuid) // if the referenced event does not exist we might as well try to send
    )
  }
  async cleanup(): Promise<{ success: boolean }> {
    return new Promise(async (resolve, reject) => {
      try {
        for await (const file of this.files) {
          if (file.uri && file?.uri?.length > 0) {
            await FileManager.delete(file.uri)
          }
        }
        resolve({ success: true })
      } catch (error) {
        reject({ success: false, error })
      }
    })
  }
  toJSON(): TDehydratedTravelLog {
    return {
      travel_log_data: this.travel_log_data,
      files: this.files,
      meta: this.meta,
      type: this.type,
    }
  }
  static fromJSON(dehydratedTravelLog: TDehydratedTravelLog): TravelLog {
    return new TravelLog(
      dehydratedTravelLog.travel_log_data,
      dehydratedTravelLog.files,
      dehydratedTravelLog.meta,
    )
  }

  static fromServerObject(
    travelLogResponse: TravelLogResponse,
    employee_id: number,
  ): TravelLog {
    const assigned_files = travelLogResponse.files?.map((f) => {
      return {
        id: f.id,
        webPath: f.web_path ?? '',
        uri: null,
        unassigned_uploaded_file_id: null, // files that come from the server are already assigned and uploaded
        mimeType: f.mime_type ?? '',
        name: f.name ?? '',
      }
    })
    return new TravelLog(
      {
        event_uuid: '',
        event_id: travelLogResponse.event_id,
        id: travelLogResponse.id,
        employee_id: employee_id,
        vehicle_type: travelLogResponse.vehicle_type,
        distance_in_km: travelLogResponse.distance_in_km,
        net_expenses_in_cent: travelLogResponse.net_expenses_in_cent,
        tax_in_cent: travelLogResponse.tax_in_cent,
        currency: travelLogResponse.currency,
        km_reading_start: travelLogResponse.km_reading_start,
        km_reading_end: travelLogResponse.km_reading_end,
        comment: travelLogResponse.comment,
        license_plate: travelLogResponse.license_plate,
        unassigned_uploaded_file_ids: [],
      },
      assigned_files,
    )
  }

  update(travelLogData: TravelLogData, files: TTravelLogFile[] = []) {
    this.travel_log_data = travelLogData
    this.files = files
  }
}
export { TravelLog, type TDehydratedTravelLog, type TTravelLogFile }
